/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Detector for Python frameworks (Streamlit, FastAPI, Flask, Django)
 */
import type { DetectorResult } from '../types/index.js';
import type { Detector } from './base.js';
import { fileExists, findFiles } from '../utils/fs.js';
import { calculateConfidence, type ConfidenceFactors } from '../core/confidence.js';
import { createDetectorResult } from './base.js';
import { join } from 'path';

export class PythonDetector implements Detector {
  name = 'python';

  async detect(cwd: string): Promise<DetectorResult> {
    // Check for Python project indicators
    const hasRequirementsTxt = fileExists(join(cwd, 'requirements.txt'));
    const hasPyprojectToml = fileExists(join(cwd, 'pyproject.toml'));
    const hasSetupPy = fileExists(join(cwd, 'setup.py'));
    const hasPipfile = fileExists(join(cwd, 'Pipfile'));

    if (!hasRequirementsTxt && !hasPyprojectToml && !hasSetupPy && !hasPipfile) {
      return createDetectorResult(this.name, 0);
    }

    // Detect virtual environment type
    const venvType = this.detectVirtualEnv(cwd);

    // Check for framework-specific files and dependencies
    const streamlitResult = await this.detectStreamlit(cwd, venvType);
    if (streamlitResult.confidence > 0) return streamlitResult;

    const fastapiResult = await this.detectFastAPI(cwd, venvType);
    if (fastapiResult.confidence > 0) return fastapiResult;

    const flaskResult = await this.detectFlask(cwd, venvType);
    if (flaskResult.confidence > 0) return flaskResult;

    const djangoResult = await this.detectDjango(cwd, venvType);
    if (djangoResult.confidence > 0) return djangoResult;

    // Generic Python project
    const entryPoint = await this.findEntryPoint(cwd);
    if (entryPoint) {
      const factors: ConfidenceFactors = {
        entryPointExists: 1,
        packageJsonExists: hasRequirementsTxt || hasPyprojectToml ? 1 : 0,
      };

      const confidence = calculateConfidence(factors);

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: 'python',
        args: [entryPoint],
        detectedType: 'Python Script',
        reasons: [
          'Detected Python project',
          `Entry point: ${entryPoint}`,
          `Virtual environment: ${venvType || 'none'}`,
        ],
      });
    }

    return createDetectorResult(this.name, 0);
  }

  /**
   * Detect virtual environment type
   */
  private detectVirtualEnv(cwd: string): string | null {
    if (fileExists(join(cwd, 'venv')) || fileExists(join(cwd, '.venv'))) {
      return 'venv';
    }
    if (fileExists(join(cwd, 'Pipfile'))) {
      return 'pipenv';
    }
    if (fileExists(join(cwd, 'poetry.lock'))) {
      return 'poetry';
    }
    if (fileExists(join(cwd, 'conda.yaml')) || fileExists(join(cwd, 'environment.yml'))) {
      return 'conda';
    }
    return null;
  }

  /**
   * Detect Streamlit app
   */
  private async detectStreamlit(cwd: string, venvType: string | null): Promise<DetectorResult> {
    const hasStreamlitDep = await this.hasPythonDependency(cwd, 'streamlit');
    const streamlitFiles = await findFiles(['*streamlit*.py', 'app.py', 'main.py'], cwd);

    if (hasStreamlitDep && streamlitFiles.length > 0) {
      const entryPoint = streamlitFiles[0];
      const pythonCmd = this.getPythonCommand(venvType);

      const factors: ConfidenceFactors = {
        frameworkDependency: 1,
        entryPointExists: 1,
        scriptExists: 1,
      };

      const confidence = calculateConfidence(factors);

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: pythonCmd,
        args: ['-m', 'streamlit', 'run', entryPoint],
        detectedType: 'Streamlit',
        reasons: [
          'Detected Streamlit app',
          `Entry point: ${entryPoint}`,
          `Using: ${pythonCmd}`,
        ],
      });
    }

    return createDetectorResult(this.name, 0);
  }

  /**
   * Detect FastAPI app
   */
  private async detectFastAPI(cwd: string, venvType: string | null): Promise<DetectorResult> {
    const hasFastapiDep = await this.hasPythonDependency(cwd, 'fastapi');
    const hasUvicorn = await this.hasPythonDependency(cwd, 'uvicorn');
    const apiFiles = await findFiles(['main.py', 'app.py', 'api.py', 'server.py'], cwd);

    if (hasFastapiDep && apiFiles.length > 0) {
      const entryPoint = apiFiles[0];
      const pythonCmd = this.getPythonCommand(venvType);
      const appModule = entryPoint.replace('.py', '').replace(/\//g, '.');

      const factors: ConfidenceFactors = {
        frameworkDependency: 1,
        entryPointExists: 1,
        scriptExists: hasUvicorn ? 1 : 0,
      };

      const confidence = calculateConfidence(factors);

      if (hasUvicorn) {
        return createDetectorResult(this.name, confidence, {
          cwd,
          pm: null,
          cmd: pythonCmd,
          args: ['-m', 'uvicorn', `${appModule}:app`, '--reload'],
          detectedType: 'FastAPI',
          reasons: [
            'Detected FastAPI project',
            `Entry point: ${entryPoint}`,
            'Using Uvicorn ASGI server',
          ],
        });
      } else {
        return createDetectorResult(this.name, confidence - 10, {
          cwd,
          pm: null,
          cmd: pythonCmd,
          args: [entryPoint],
          detectedType: 'FastAPI',
          reasons: [
            'Detected FastAPI project',
            `Entry point: ${entryPoint}`,
            'Note: Install uvicorn for better dev experience',
          ],
        });
      }
    }

    return createDetectorResult(this.name, 0);
  }

  /**
   * Detect Flask app
   */
  private async detectFlask(cwd: string, venvType: string | null): Promise<DetectorResult> {
    const hasFlaskDep = await this.hasPythonDependency(cwd, 'flask');
    const flaskFiles = await findFiles(['app.py', 'application.py', 'wsgi.py', 'main.py'], cwd);

    if (hasFlaskDep && flaskFiles.length > 0) {
      const entryPoint = flaskFiles[0];
      const pythonCmd = this.getPythonCommand(venvType);

      const factors: ConfidenceFactors = {
        frameworkDependency: 1,
        entryPointExists: 1,
        scriptExists: 1,
      };

      const confidence = calculateConfidence(factors);

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: pythonCmd,
        args: [entryPoint],
        env: {
          FLASK_APP: entryPoint,
          FLASK_ENV: 'development',
        },
        detectedType: 'Flask',
        reasons: [
          'Detected Flask application',
          `Entry point: ${entryPoint}`,
          'Development mode enabled',
        ],
      });
    }

    return createDetectorResult(this.name, 0);
  }

  /**
   * Detect Django app
   */
  private async detectDjango(cwd: string, venvType: string | null): Promise<DetectorResult> {
    const hasDjangoDep = await this.hasPythonDependency(cwd, 'django');
    const hasManagePy = fileExists(join(cwd, 'manage.py'));

    if (hasDjangoDep && hasManagePy) {
      const pythonCmd = this.getPythonCommand(venvType);

      const factors: ConfidenceFactors = {
        frameworkDependency: 1,
        entryPointExists: 1,
        configFileExists: 1,
      };

      const confidence = calculateConfidence(factors);

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: pythonCmd,
        args: ['manage.py', 'runserver'],
        detectedType: 'Django',
        reasons: [
          'Detected Django project',
          'Using manage.py runserver',
          'Development server mode',
        ],
      });
    }

    return createDetectorResult(this.name, 0);
  }

  /**
   * Check if a Python dependency exists
   */
  private async hasPythonDependency(cwd: string, depName: string): Promise<boolean> {
    // Check requirements.txt
    const requirementsPath = join(cwd, 'requirements.txt');
    if (fileExists(requirementsPath)) {
      try {
        const fs = await import('fs/promises');
        const content = await fs.readFile(requirementsPath, 'utf-8');
        if (content.toLowerCase().includes(depName.toLowerCase())) {
          return true;
        }
      } catch {
        // Ignore read errors
      }
    }

    // Check pyproject.toml
    const pyprojectPath = join(cwd, 'pyproject.toml');
    if (fileExists(pyprojectPath)) {
      try {
        const fs = await import('fs/promises');
        const content = await fs.readFile(pyprojectPath, 'utf-8');
        if (content.toLowerCase().includes(depName.toLowerCase())) {
          return true;
        }
      } catch {
        // Ignore read errors
      }
    }

    // Check Pipfile
    const pipfilePath = join(cwd, 'Pipfile');
    if (fileExists(pipfilePath)) {
      try {
        const fs = await import('fs/promises');
        const content = await fs.readFile(pipfilePath, 'utf-8');
        if (content.toLowerCase().includes(depName.toLowerCase())) {
          return true;
        }
      } catch {
        // Ignore read errors
      }
    }

    return false;
  }

  /**
   * Find Python entry point
   */
  private async findEntryPoint(cwd: string): Promise<string | null> {
    const entryPoints = ['main.py', 'app.py', 'run.py', '__main__.py'];

    for (const entry of entryPoints) {
      if (fileExists(join(cwd, entry))) {
        return entry;
      }
    }

    // Look for any .py file in root
    const pyFiles = await findFiles('*.py', cwd);
    return pyFiles.length > 0 ? pyFiles[0] : null;
  }

  /**
   * Get Python command based on virtual environment
   */
  private getPythonCommand(venvType: string | null): string {
    switch (venvType) {
      case 'venv':
        return process.platform === 'win32' ? 'venv\\Scripts\\python' : 'venv/bin/python';
      case 'pipenv':
        return 'pipenv run python';
      case 'poetry':
        return 'poetry run python';
      case 'conda':
        return 'conda run python';
      default:
        return 'python';
    }
  }
}

